import { useGetCompanyNameLazyQuery } from 'generated/graphql';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import DynamicSubHeader from '@ifca-root/react-component/src/components/Header/DynamicSubHeader';
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader';
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { smallTitle } from '@ifca-root/react-component/src/helpers/Global/ContractGlobalVar';
import {
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
} from '@material-ui/core';
import { KeyboardArrowRight } from '@material-ui/icons';
import { ErrorDialog } from 'components/Dialog/ErrorDialog';
import { useGetStandardLetterLazyQuery } from 'generated/graphql';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { StyledMenu, StyledMenuItem } from '@ifca-root/react-component/src/components/others/Menu';

interface Props {
  mode: 'general-settings' | 'company-setup';
}

const categoryOptions = [
  { value: 'supplier', label: smallTitle.SUPPLIER_ACCOUNTS },
  { value: 'client-contract', label: smallTitle.PROCUMENT_MANAGEMENT },
];

export const StandardLetterListing = (props: Props) => {
  const { mode } = props;
  const { pageMode: urlPageMode, companyID }: any = useParams();

  const [pageMode, setPageMode] = useState<string>(urlPageMode ?? 'all');

  let history = useHistory();
  const user = JSON.parse(localStorage.getItem('loggedInUser'));
  const [errMessage, setErrMessage] = useState(null);
  const [errDialog, setErrDialog] = useState(false);
  const [combinedTemplates, setCombinedTemplates] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('supplier');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Separate query hooks for each category

  const [
    fetchSupplier,
    { loading: loadingSupplier, data: supplierData },
  ] = useGetStandardLetterLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
  });

  const [
    fetchProjectPurchase,
    { loading: loadingProject, data: projectData },
  ] = useGetStandardLetterLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
  });

  // General Settings fetch
  const [
    fetchStdLetter,
    {
      loading: standardLetterLoading,
      data: { getStandardLetter } = { getStandardLetter: [] },
    },
  ] = useGetStandardLetterLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
  });

  const [
    fetchCompany,
    { loading: companyLoading, data: { getCompany } = { getCompany: [] } },
  ] = useGetCompanyNameLazyQuery({
    fetchPolicy: 'network-only',
    variables: { companyID },
    onError: ({ message }) => {
      let error = message?.substring(15);
      setErrMessage(error);
      setErrDialog(true);
    },
  });

  let headerTitle: string = '';
  switch (pageMode) {
    case 'client-account':
      headerTitle = smallTitle.CLIENT_ACCOUNTS;
      break;
    case 'subcontractor':
      headerTitle = smallTitle.SUBCON_ACCOUNTS;
      break;
    case 'supplier':
      headerTitle = smallTitle.SUPPLIER_ACCOUNTS;
      break;
    case 'client-contract':
      headerTitle = smallTitle.PROJECT_PURCHASES;
      break;
    default:
      headerTitle = smallTitle.PRODUCT;
      break;
  }

  const getCategoryLabel = (value: string) => {
    return (
      categoryOptions.find(option => option.value === value)?.label ||
      'All Categories'
    );
  };

  // Dropdown handlers
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setPageMode(category);
    handleMenuClose();
  };

  const getFilteredTemplates = () => {
    switch (selectedCategory) {
      case 'supplier':
        return supplierData?.getStandardLetter || [];
      case 'client-contract':
        return projectData?.getStandardLetter || [];
      case 'all':
        return combinedTemplates;
      default:
        return combinedTemplates;
    }
  };
  const displayTemplates = getFilteredTemplates();

  const isLoading =
    loadingAll ||
    loadingSupplier ||
    loadingProject ||
    standardLetterLoading;

  // Combine all templates when all category data is available
  useEffect(() => {
    if (
      supplierData &&
      projectData
    ) {
      const allTemplates = [
        ...(supplierData.getStandardLetter || []),
        ...(projectData.getStandardLetter || []),
      ];

      // Remove duplicates based on ID
      const uniqueTemplates = allTemplates.filter(
        (template, index, self) =>
          index === self.findIndex(t => t.ID === template.ID),
      );

      setCombinedTemplates(uniqueTemplates);
      setLoadingAll(false);
    }
  }, [supplierData, projectData, mode, pageMode]);

  useEffect(() => {

    if (pageMode === 'all') {
      // For company-setup mode, fetch all categories individually
      setLoadingAll(true);
      fetchSupplier({ variables: { letterCategory: 'SUPPLIER_ACCOUNT' } });
      fetchProjectPurchase({
        variables: { letterCategory: 'PROJECT_PURCHASE' },
      });

    } else {

      if (pageMode === 'client-account') {
        fetchStdLetter({ variables: { letterCategory: 'CLIENT_ACCOUNT' } });
      } else if (pageMode === 'contractor') {
        fetchStdLetter({
          variables: { letterCategory: 'CONTRACTOR_ACCOUNT' },
        });
      } else if (pageMode === 'supplier') {
        fetchStdLetter({ variables: { letterCategory: 'SUPPLIER_ACCOUNT' } });
      } else if (pageMode === 'client-contract') {
        fetchStdLetter({ variables: { letterCategory: 'PROJECT_PURCHASE' } });
      }
    }

    if (mode === 'company-setup') {
      fetchCompany({ variables: { companyID } });
    }
  }, [pageMode]);

  return (
    <>
      {companyLoading && <Loading />}
      {standardLetterLoading && <Loading />}
      <MainHeader
        mainBtn="back"
        onClick={() => {
          history.push({
            pathname:
              mode === 'general-settings'
                ? `/general-settings`
                : `/company-setup/submenu/${companyID}`,
          });
        }}
        smTitle={headerTitle}
        title={
          mode === 'general-settings' ? user?.companyName : getCompany[0]?.Name
        }
        routeSegments={
          mode === 'general-settings'
            ? [
              { name: 'System Admin' },
              { name: 'System Admin' },
              { name: 'Document Template', current: true },
            ]
            : [
              { name: 'System Admin' },
              { name: 'Company Document Template', current: true },
            ]
        }
      />
      <>
        <DynamicSubHeader
          title={mode === 'company-setup' && getCompany[0]?.Name}
          rightText={mode === 'company-setup' && getCompany[0]?.CompanyRegNo}
          infoLine={getCategoryLabel(selectedCategory)}
          infoLineHandleClick={handleMenuClick}
          infoLineDropDownOptions={
            // @ts-expect-error
            <StyledMenu
              id="category-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {categoryOptions.map(option => (
                // @ts-expect-error
                <StyledMenuItem
                  key={option.value}
                  selected={option.value === selectedCategory}
                  onClick={() => {
                    handleCategorySelect(option.value);
                    handleMenuClose();
                  }}
                >
                  <ListItemText primary={option.label} />
                </StyledMenuItem>
              ))}
            </StyledMenu>
          }
        />
        <div style={{ paddingBottom: '10px' }} />
      </>

      <ContentWrapper
        singleDynamicInfo
      >
        {!isLoading && displayTemplates?.length === 0 ? (
          <EmptyList
            title="No Document Template"
            subtitle="Please create new Document Template"
          />
        ) : (
          <List className="core-list">
            {displayTemplates?.map((v, i) => (
              <ListItem
                button
                onClick={() =>
                  history.push({
                    pathname:
                      mode === 'general-settings'
                        ? `/general-settings/standard-letter/${v?.ID}/letter-template`
                        : `/company-setup/submenu/${companyID}/company-standard-letter/${v?.ID}/company-letter-template`,
                  })
                }
              >
                <ListItemText
                  primary={<div className="xsTitle">{v?.name}</div>}
                  secondary={
                    <span className="desc">
                      {mode === 'company-setup'
                        ? v?.companyLetterTemplateSubscription?.filter(
                          x =>
                            x?.companyID === companyID &&
                            x?.commonStatus === 'ACTIVE',
                        )?.length ?? 0
                        : v?.letterTemplateSubscription?.length ?? 0}{' '}
                      Template(s)
                    </span>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" aria-label="delete">
                    <KeyboardArrowRight
                      onClick={() =>
                        history.push({
                          pathname:
                            mode === 'general-settings'
                              ? `/general-settings/standard-letter/${v?.ID}/letter-template`
                              : `/company-setup/submenu/${companyID}/company-standard-letter/${v?.ID}/company-letter-template`,
                        })
                      }
                    />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </ContentWrapper>
      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />
    </>
  );
};
